import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
    Mail, 
    ArrowRight, 
    ShieldCheck, 
    Sparkles, 
    CheckCircle2, 
    AlertCircle, 
    Loader2, 
    ChevronRight,
    BookOpen,
    Smile,
    Calendar,
    Zap,
    Download
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { getAttributionPayload, initAttribution } from '@/lib/attribution';

const getApiUrl = () => {
    if (typeof window === 'undefined') return 'http://localhost:8001/api';
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:8001/api';
    }
    return `${window.location.origin}/api`;
};
const API = getApiUrl();

const DEFAULT_PREVIEW_PAGES = [
    { 
        id: 'cover', 
        title: 'Front Cover', 
        label: 'Front Cover', 
        src: '/magazine_previews/news/cover_v01.webp', 
        desc: "The week's biggest AI news, in 5 minutes. Free every Monday." 
    },
    { 
        id: 'p03', 
        title: 'OpenAI Mega-Round', 
        label: 'Inside Teaser · Page 3', 
        src: '/magazine_previews/news/page_03_v01.webp', 
        desc: 'Cover Story: OpenAI chases $30B at a $1.25T valuation before its planned listing.' 
    }
];

export default function SubscribePage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [bottomEmail, setBottomEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('idle'); // 'idle' | 'success' | 'already' | 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const [activePreview, setActivePreview] = useState(0);
    const [latestMag, setLatestMag] = useState(null);
    const [previewPages, setPreviewPages] = useState(DEFAULT_PREVIEW_PAGES);

    // 50/50 headline A/B test with sticky localStorage key
    const [headlineVariant] = useState(() => {
        try {
            let v = localStorage.getItem('tnf_sub_variant');
            if (!v || (v !== 'A' && v !== 'B')) {
                v = Math.random() < 0.5 ? 'A' : 'B';
                localStorage.setItem('tnf_sub_variant', v);
            }
            return v;
        } catch (e) {
            return 'A';
        }
    });

    useEffect(() => {
        document.title = "The Neural Feed Weekly: the week's biggest AI news, free every Monday";
        
        // Update meta description
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute('content', "The week's AI news that actually mattered, in a 5-minute magazine. 6–8 AI stories every Monday, plus AI-made comics at the back. Free.");
        }

        window.scrollTo(0, 0);

        // Fetch latest weekly news issue metadata dynamically
        axios.get(`${API}/magazines/latest`)
            .then(res => {
                if (res.data?.news) {
                    setLatestMag(res.data.news);
                } else if (res.data?.toons) {
                    setLatestMag(res.data.toons);
                }
            })
            .catch(err => {
                console.log('Using default magazine issue:', err);
            });

        // Check dynamic news previews manifest
        axios.get('/magazine_previews/news/previews.json')
            .then(res => {
                if (Array.isArray(res.data?.pages) && res.data.pages.length > 0) {
                    setPreviewPages(res.data.pages);
                }
            })
            .catch(() => {
                // Keep default preview pages
            });
    }, []);

    useEffect(() => {
        initAttribution();
    }, []);

    const handleSubscribe = async (inputEmail) => {
        const cleanEmail = (inputEmail || '').trim().toLowerCase();
        if (!cleanEmail || !cleanEmail.includes('@')) {
            setErrorMessage('Please enter a valid email address.');
            setStatus('error');
            return;
        }

        setLoading(true);
        setStatus('idle');
        setErrorMessage('');

        const subVariantTag = headlineVariant === 'B' ? 'sub_head_B' : 'sub_head_A';
        const attr = getAttributionPayload('/subscribe');
        if (!attr.utm_content) {
            attr.utm_content = subVariantTag;
        }
        const utmSource = attr.utm_source || 'direct';
        const utmMedium = attr.utm_medium || 'web';
        const utmCampaign = attr.utm_campaign || 'none';

        try {
            await axios.post(`${API}/subscribers`, { 
                email: cleanEmail,
                ...attr
            });
            localStorage.setItem('tnf_subscribed', 'true');
            setStatus('success');

            // Fire signup event to Google Analytics
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'signup', {
                    method: 'news_magazine_page',
                    utm_source: utmSource,
                    utm_medium: utmMedium,
                    utm_campaign: utmCampaign,
                    utm_content: subVariantTag
                });
            }

            // Fire signup event to PostHog
            if (window.posthog) {
                window.posthog.capture('signup', {
                    method: 'news_magazine_page',
                    utm_source: utmSource,
                    utm_medium: utmMedium,
                    utm_campaign: utmCampaign,
                    utm_content: subVariantTag
                });
            }
        } catch (err) {
            console.error('Subscription error:', err);
            const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
            if (msg.toLowerCase().includes('already')) {
                setStatus('already');
            } else {
                setStatus('error');
                setErrorMessage(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#070B14] text-slate-100 flex flex-col justify-between selection:bg-amber-500/30 selection:text-amber-200">
            {/* Top Header */}
            <header className="border-b border-slate-800/80 bg-[#0B1120]/90 backdrop-blur-md sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 group">
                        <img
                            src="/logo192.png"
                            alt="The Neural Feed"
                            className="w-9 h-9 rounded-lg object-cover border border-emerald-500/30 shadow-sm transition-transform group-hover:scale-105"
                        />
                        <div className="flex flex-col">
                            <span className="font-black tracking-tight text-base text-white group-hover:text-amber-400 transition-colors">
                                THE NEURAL FEED
                            </span>
                            <span className="text-[10px] font-mono tracking-wider text-slate-400 uppercase -mt-0.5">
                                WEEKLY AI MAGAZINE
                            </span>
                        </div>
                    </Link>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/issues"
                            className="hidden sm:inline-flex items-center text-xs font-semibold text-slate-300 hover:text-amber-400 transition-colors"
                        >
                            Past Issues
                        </Link>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/')}
                            className="text-slate-300 hover:text-white hover:bg-slate-800/60 rounded-full text-xs font-semibold"
                        >
                            Browse Today's News <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Page Content */}
            <main className="flex-1 max-w-4xl mx-auto px-4 py-10 md:py-14 w-full flex flex-col items-center">
                
                {/* 1. Hero Headline — Leading with the News Magazine */}
                <div className="text-center max-w-2xl mx-auto mb-8">
                    <Badge variant="outline" className="mb-4 py-1 px-3 border-amber-500/40 bg-amber-500/10 text-amber-300 rounded-full text-xs font-mono font-semibold tracking-wide">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5 inline text-amber-400" />
                        THE NEURAL FEED WEEKLY
                    </Badge>
                    
                    <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white leading-tight mb-4 font-['Chivo',sans-serif]">
                        {headlineVariant === 'B' ? (
                            <>Missed what happened in AI this week? <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-200">We've got you.</span></>
                        ) : (
                            <>The week's biggest AI news, <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-200">in 5 minutes.</span></>
                        )}
                    </h1>
                    
                    <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mx-auto">
                        The Neural Feed Weekly lands in your inbox every Monday: 6–8 AI stories that actually mattered. Plus a page or two of AI-made comics at the back.
                    </p>
                </div>

                {/* 2. Top Signup Card */}
                <div id="top-signup" className="w-full max-w-xl mx-auto mb-14">
                    <Card className="border border-slate-700/80 bg-gradient-to-b from-[#0F172A] to-[#0B1120] shadow-2xl rounded-2xl overflow-hidden relative">
                        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-500 via-emerald-400 to-teal-400" />

                        <CardContent className="p-6 sm:p-8">
                            {status === 'idle' || status === 'error' ? (
                                <form onSubmit={(e) => { e.preventDefault(); handleSubscribe(email); }} className="space-y-4">
                                    <div className="space-y-2">
                                        <label htmlFor="top-email" className="block text-xs font-mono font-bold tracking-wider uppercase text-slate-300">
                                            Where should we send it?
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <Input
                                                id="top-email"
                                                type="email"
                                                required
                                                autoFocus
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="you@example.com"
                                                className="pl-10 h-12 bg-slate-900/90 border-slate-700 text-white placeholder:text-slate-500 text-base rounded-xl focus-visible:ring-amber-500"
                                            />
                                        </div>
                                    </div>

                                    {status === 'error' && errorMessage && (
                                        <div className="p-3 bg-red-950/40 border border-red-800/60 rounded-lg text-red-300 text-xs flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                                            <span>{errorMessage}</span>
                                        </div>
                                    )}

                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full h-12 text-sm sm:text-base font-bold bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-slate-950 rounded-xl shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin text-slate-950" />
                                                Preparing Your Issue...
                                            </>
                                        ) : (
                                            <>
                                                Send me Monday's issue →
                                            </>
                                        )}
                                    </Button>

                                    <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1">
                                        <span className="flex items-center gap-1.5">
                                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                                            PDF • 5-minute read
                                        </span>
                                        <span>Free. One email every Monday. Unsubscribe anytime.</span>
                                    </div>
                                </form>
                            ) : (
                                /* Success State */
                                <div className="text-center py-4 space-y-5 animate-in fade-in duration-300">
                                    <div className="w-14 h-14 bg-emerald-500/20 border border-emerald-500/40 rounded-full flex items-center justify-center mx-auto text-emerald-400 shadow-inner">
                                        <CheckCircle2 className="w-8 h-8" />
                                    </div>

                                    <div className="space-y-1">
                                        <h2 className="text-2xl font-black text-white">
                                            {status === 'already' ? "You're Already on the List!" : "Done! This week's issue is on its way to your inbox."}
                                        </h2>
                                        <p className="text-sm text-slate-300 max-w-sm mx-auto">
                                            {status === 'already' 
                                                ? "Your subscription is active! Check your inbox for your Monday edition."
                                                : "Check your inbox for your welcome email containing this week's issue."}
                                        </p>
                                    </div>

                                    <div className="pt-2">
                                        <Button
                                            onClick={() => navigate('/')}
                                            variant="outline"
                                            className="w-full h-11 border-slate-700 hover:bg-slate-800 text-slate-200 text-xs font-semibold rounded-xl"
                                        >
                                            Browse Today's AI News Feed <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* 3. Real Cover & Interior Page Previews Gallery */}
                <div className="w-full max-w-3xl mb-16">
                    <div className="text-center mb-6">
                        <h2 className="text-xs font-mono font-bold tracking-widest text-slate-400 uppercase">
                            A LOOK INSIDE THIS WEEK'S ISSUE
                        </h2>
                        <p className="text-sm text-slate-300 mt-1">
                            The cover story, the rest of the week's news, and the comics at the back.
                        </p>
                    </div>

                    {/* Preview Tabs */}
                    <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
                        {previewPages.map((page, idx) => (
                            <button
                                key={page.id || idx}
                                onClick={() => setActivePreview(idx)}
                                className={`py-1.5 px-3 rounded-full text-xs font-medium transition-all ${
                                    activePreview === idx
                                        ? 'bg-amber-500 text-slate-950 font-bold shadow-md'
                                        : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700 hover:text-white'
                                }`}
                            >
                                {page.label} • {page.title}
                            </button>
                        ))}
                    </div>

                    {/* Featured Preview Viewer */}
                    {(() => {
                        const cur = previewPages[activePreview] || previewPages[0] || DEFAULT_PREVIEW_PAGES[0];
                        return (
                            <div className="p-4 sm:p-6 bg-[#0B1120] border border-slate-800 rounded-2xl shadow-2xl flex flex-col md:flex-row items-center gap-6">
                                <div className="w-full md:w-1/2 aspect-[9/12.7] rounded-xl overflow-hidden border border-slate-700/80 shadow-lg bg-slate-950 flex items-center justify-center relative group">
                                    <img
                                        src={cur.src}
                                        alt={cur.title}
                                        className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                                        loading="lazy"
                                    />
                                    <div className="absolute bottom-2 right-2 bg-slate-950/80 backdrop-blur-sm px-2 py-1 rounded text-[10px] font-mono text-slate-300 border border-slate-700/50">
                                        🔍 Sample Page
                                    </div>
                                </div>

                                <div className="w-full md:w-1/2 space-y-4 text-center md:text-left">
                                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[11px] font-mono">
                                        {cur.label}: {cur.title}
                                    </Badge>

                                    <h3 className="text-xl font-bold text-white leading-snug">
                                        {cur.desc}
                                    </h3>

                                    <p className="text-xs text-slate-300 leading-relaxed">
                                        Every Monday, we distill the week's biggest AI news into a clean, 5-minute executive briefing with verifiable data points and zero hype. Plus a page or two of visual satire at the back.
                                    </p>

                                    <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800 text-xs text-slate-400 space-y-1.5">
                                        <div className="flex items-center gap-2 text-slate-200 font-semibold">
                                            <BookOpen className="w-4 h-4 text-amber-400 shrink-0" />
                                            <span>What's inside each weekly issue:</span>
                                        </div>
                                        <ul className="list-disc list-inside space-y-1 text-slate-400 pl-1">
                                            <li>One deep cover story breakdown</li>
                                            <li>6–8 AI stories that actually mattered</li>
                                            <li>Bonus: AI-made tech comics at the back</li>
                                        </ul>
                                    </div>

                                    <a
                                        href="#top-signup"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            document.getElementById('top-email')?.focus();
                                            window.scrollTo({ top: 120, behavior: 'smooth' });
                                        }}
                                        className="inline-flex items-center text-xs font-bold text-amber-400 hover:text-amber-300 transition-colors gap-1 pt-1"
                                    >
                                        Subscribe free to read the full issue <ChevronRight className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* 4. Clear Breakdown: How Your Subscription Works */}
                <div className="w-full max-w-3xl mb-16">
                    <h2 className="text-xs font-mono font-bold tracking-widest text-slate-400 uppercase text-center mb-6">
                        HOW YOUR SUBSCRIPTION WORKS
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Monday News Magazine Drop */}
                        <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1E1B4B]/30 to-[#0F172A] border border-amber-500/30 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-500/20 text-amber-300 uppercase">
                                    EVERY MONDAY MORNING
                                </span>
                                <BookOpen className="w-5 h-5 text-amber-400" />
                            </div>
                            <h3 className="font-bold text-base text-white">The Neural Feed Weekly</h3>
                            <p className="text-xs text-slate-300 leading-relaxed">
                                The week's biggest AI news in one short magazine: a cover story, 6–8 AI stories, and a page or two of AI-made comics as a bonus.
                            </p>
                        </div>

                        {/* Weekday Brief */}
                        <div className="p-5 rounded-2xl bg-gradient-to-br from-[#064E3B]/20 to-[#0F172A] border border-emerald-500/30 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-emerald-500/20 text-emerald-400 uppercase">
                                    WEEKDAY MORNINGS
                                </span>
                                <Zap className="w-5 h-5 text-emerald-400" />
                            </div>
                            <h3 className="font-bold text-base text-white">5-Minute AI News Digest</h3>
                            <p className="text-xs text-slate-300 leading-relaxed">
                                Top 10 vetted stories scanned from 165+ developer feeds and research preprints, paired with daily visual Neural Snap cards so you stay informed in minutes.
                            </p>
                        </div>
                    </div>
                </div>

                {/* 5. Second Signup Box at Bottom */}
                <div className="w-full max-w-xl mx-auto mb-10">
                    <Card className="border border-slate-700/80 bg-gradient-to-b from-[#0F172A] to-[#070B14] shadow-2xl rounded-2xl overflow-hidden relative">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-yellow-400" />

                        <CardContent className="p-6 sm:p-8 text-center space-y-4">
                            <h3 className="text-xl sm:text-2xl font-black text-white font-['Chivo',sans-serif]">
                                Ready for Monday's issue?
                            </h3>
                            <p className="text-xs sm:text-sm text-slate-300 max-w-md mx-auto">
                                Enter your email and this week's issue is on its way.
                            </p>

                            <form onSubmit={(e) => { e.preventDefault(); handleSubscribe(bottomEmail); }} className="space-y-3 text-left">
                                <div className="relative">
                                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        type="email"
                                        required
                                        value={bottomEmail}
                                        onChange={(e) => setBottomEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        className="pl-10 h-12 bg-slate-900/90 border-slate-700 text-white placeholder:text-slate-500 text-base rounded-xl focus-visible:ring-amber-500"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-12 text-sm sm:text-base font-bold bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-slate-950 rounded-xl shadow-lg transition-all"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin text-slate-950" />
                                            Activating...
                                        </>
                                    ) : (
                                        <>
                                            Send me Monday's issue →
                                        </>
                                    )}
                                </Button>
                            </form>

                            <p className="text-[11px] text-slate-400">
                                Free. One email every Monday. Unsubscribe anytime.
                            </p>
                        </CardContent>
                    </Card>
                </div>

            </main>

            {/* Footer */}
            <footer className="border-t border-slate-800/80 py-8 text-center text-xs text-slate-400 space-y-2">
                <p>© 2026 The Neural Feed. Independent AI news, every Monday.</p>
                <div className="flex items-center justify-center gap-4 text-slate-400">
                    <Link to="/" className="hover:text-slate-200 transition-colors">Daily News Feed</Link>
                    <span>•</span>
                    <Link to="/issues" className="hover:text-slate-200 transition-colors">Past Issues</Link>
                    <span>•</span>
                    <Link to="/privacy" className="hover:text-slate-200 transition-colors">Privacy Policy</Link>
                    <span>•</span>
                    <a href="mailto:contact@theneuralfeed.com" className="hover:text-slate-200 transition-colors">Contact</a>
                </div>
            </footer>
        </div>
    );
}
